/**
 * Whom to shoot and with what: target selection (skill-gated factors,
 * personality-weighted), the fog-aware candidate list, and weapon choice.
 */

import type { PlayerState, GameState, BotConfig, BotFogContext, BotPersonality, WeaponKey, Vec3, PlanetState, GhostMarker } from '../../types';
import { vecLength, vecSub, vecNormalize } from '../../math';
import { WEAPONS } from '../../constants';
import type { SeededRandom } from '../../core/random';
import { getPlayerWorldPos, hasAmmo, getAvailableWeapons, getAliveEnemies } from './common';
import { tuningFor, type BotTuning } from './tuning';

// =============================================================================
// TARGET SELECTION
// =============================================================================

/**
 * Select which enemy to target using multi-factor scoring.
 * Skill gates which factors are used; personality weights their fusion.
 */
export function selectTarget(
  player: PlayerState,
  gameState: GameState,
  config: BotConfig,
  rng: SeededRandom,
  fogContext?: BotFogContext
): PlayerState | null {
  // Command override: fireAtTarget → return that target directly if alive
  const cmd = player.pendingCommand;
  if (cmd?.type === 'fireAtTarget' && cmd.targetPlayerId !== undefined) {
    const cmdTarget = gameState.players.find(
      p => p.id === cmd.targetPlayerId && p.alive && p.teamIndex !== player.teamIndex
    );
    if (cmdTarget) return cmdTarget;
    // Target dead/invalid — fall through to normal selection
  }

  // Get candidate enemies — fog-filtered if context provided; a target the planner found wasted is skipped if another remains
  let candidates = getTargetableEnemies(player, gameState, fogContext);
  const avoid = fogContext?.round?.avoidTargetIds;
  if (avoid && avoid.length > 0) { const rest = candidates.filter(c => !avoid.includes(c.id)); if (rest.length > 0) candidates = rest; }
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];

  const myPos = getPlayerWorldPos(player, gameState.planets);
  const { skill, personality } = config;
  const tuning = tuningFor(config);
  const t = tuning.targeting;
  const round = fogContext?.round;

  // Build per-candidate scores
  let best = candidates[0];
  let bestScore = -Infinity;
  const scored: Array<{ enemy: PlayerState; score: number }> = [];

  for (const enemy of candidates) {
    const enemyPos = getPlayerWorldPos(enemy, gameState.planets);
    const dist = vecLength(vecSub(enemyPos, myPos));

    // Factor 1: Distance (always available)
    const distScore = 1 / (1 + dist * t.distanceFalloff);

    // Factor 2: Health (skill >= 0.3)
    const healthScore = (100 - enemy.health) / 100;

    // Factor 3: Threat — who dealt most damage to us (skill >= 0.6)
    let threatScore = 0;
    if (skill >= t.threatMinSkill) {
      // Approximate threat: enemies on same or adjacent planets who have dealt damage
      const enemyOnSamePlanet = enemy.planetIndex === player.planetIndex;
      threatScore = (enemy.damageDealt / Math.max(1, gameState.turnNumber * t.threatTurnScale));
      if (enemyOnSamePlanet) threatScore += t.threatSamePlanetBonus;
    }

    // Factor 4: Reachability (skill >= 0.6)
    let reachScore = t.reachFar; // default: far
    if (skill >= t.threatMinSkill) {
      if (enemy.planetIndex === player.planetIndex) {
        reachScore = 1.0; // same planet
      } else {
        // Adjacent = close planets
        const myPlanetPos = gameState.planets[player.planetIndex].position;
        const enemyPlanetPos = gameState.planets[enemy.planetIndex].position;
        const planetDist = vecLength(vecSub(enemyPlanetPos, myPlanetPos));
        reachScore = planetDist < t.reachAdjacentDistance ? t.reachAdjacent : t.reachFar;
      }
    }

    // Fuse factors based on skill gating
    let score: number;
    if (skill < t.healthMinSkill) {
      // Low skill: only distance + heavy noise
      score = distScore;
    } else if (skill < t.threatMinSkill) {
      // Medium skill: distance + health
      score = distScore * t.mediumDistanceWeight + healthScore * t.mediumHealthWeight;
    } else {
      // High skill: all 4 factors with personality weights
      score = personality.aggression * (healthScore * t.aggressiveHealthWeight + threatScore * t.aggressiveThreatWeight)
            + (1 - personality.aggression) * (distScore * t.cautiousDistanceWeight + reachScore * t.cautiousReachWeight);
    }

    // Add noise inversely proportional to skill
    score += (rng.next() - 0.5) * (1 - skill) * t.noise;

    // Ghost penalty: stale intel makes target less reliable
    if (fogContext && !fogContext.visibleEnemyIds.includes(enemy.id)) {
      score *= t.ghostPenalty;
    }

    // Command: prioritizeEnemy gives a score bonus
    if (cmd?.type === 'prioritizeEnemy' && cmd.targetPlayerId === enemy.id) {
      score += t.prioritizeCommandBonus;
    }

    // Round flows (Phase 47): spread the team's fire; prefer enemies that cannot walk away before the round resolves
    if (round) {
      if (round.teamQueuedTargetIds.includes(enemy.id)) score *= tuning.flow.teammateQueuedTargetFactor;
      if (!round.enemyCommittedIds.includes(enemy.id)) score *= tuning.flow.uncommittedTargetFactor;
      // Expected damage: what is left to shoot at once the team's queued shots land
      const remaining = round.expectedEnemyHealth?.[enemy.id];
      if (remaining !== undefined) score *= expectedDamageScale(remaining, oneShotDamage(player), tuning.flow.deadOnPaperFactor);
    }

    scored.push({ enemy, score });
    if (score > bestScore) { bestScore = score; best = enemy; }
  }

  // The team's first planner this round: teammates who still have to plan will take the obvious target with
  // shorter flights that land first — leave it to them when the runner-up is nearly as good
  if (round && tuning.flow.firstPlannerDefers > 0 && scored.length >= 2 && !(round.teamOrders && round.teamOrders.length > 0)) {
    const planned = new Set((round.teamOrders ?? []).map(o => o.playerId));
    const stillToPlan = gameState.players.filter(p => p.alive && p.teamIndex === player.teamIndex && p.id !== player.id && !planned.has(p.id)).length;
    if (stillToPlan >= 1) {
      const ranked = [...scored].sort((a, b) => b.score - a.score);
      if (ranked[1].score >= ranked[0].score * tuning.flow.firstPlannerDefers && ranked[0].score > 0) return ranked[1].enemy;
    }
  }

  return best;
}

/** Damage one of this bot's shots is worth, for scaling a target's value: its best available weapon's base damage. */
export function oneShotDamage(player: PlayerState): number {
  let best = 0;
  for (const key of getAvailableWeapons(player)) best = Math.max(best, WEAPONS[key].damage);
  return Math.max(1, best || WEAPONS.standard.damage);
}

/** Score scale for a target with `remaining` expected health: full value while a shot's worth remains, floored when dead on paper. */
export function expectedDamageScale(remaining: number, shotDamage: number, floor: number): number {
  if (remaining <= 0) return floor;
  return Math.max(floor, Math.min(1, remaining / shotDamage));
}

/**
 * Expected remaining health per enemy after the team's queued shots, from a dry run of the queue
 * (`predicted`), discounted by what the team can legitimately know: a ghost's position is uncertain,
 * and an enemy that has not taken its planning turn may still move — a fixed prior, never its actual
 * movement points (the team cannot see those).
 */
export function expectedEnemyHealth(
  player: PlayerState,
  gameState: GameState,
  predicted: GameState,
  fogContext: BotFogContext,
  tuning: BotTuning,
): Record<number, number> {
  const round = fogContext.round;
  const out: Record<number, number> = {};
  for (const enemy of gameState.players) {
    if (!enemy.alive || enemy.teamIndex === player.teamIndex) continue;
    const after = predicted.players.find(p => p.id === enemy.id);
    const predictedHealth = after && after.alive ? after.health : 0;
    const damage = Math.max(0, enemy.health - predictedHealth);
    let confidence = 1;
    if (!fogContext.visibleEnemyIds.includes(enemy.id)) confidence *= tuning.flow.ghostDamageConfidence;
    if (round && !round.enemyCommittedIds.includes(enemy.id)) confidence *= tuning.flow.uncommittedDamageConfidence;
    let remaining = enemy.health - damage * confidence;
    // Displacement: a teammate's blast launches or moves the target, so a shot aimed where it stands now
    // lands on empty ground (the measured bulk of wasted round-flow shots) — the dry run shows it
    if (after && after.alive) {
      const moved = after.airborne || after.planetIndex !== enemy.planetIndex
        || vecLength(vecSub(getPlayerWorldPos(after, predicted.planets), getPlayerWorldPos(enemy, gameState.planets))) > 2;
      if (moved) remaining *= confidence * tuning.flow.displacedTargetConfidence + (1 - confidence);
    }
    out[enemy.id] = remaining;
  }
  return out;
}

/**
 * Where to aim at a target: where it stands, or — in a round flow, when the bot
 * leads planned walks and can see the enemy's planned-move marker — where it will
 * stand when the round resolves.
 */
export function expectedTargetPos(target: PlayerState, planets: PlanetState[], config: BotConfig, fogContext?: BotFogContext): Vec3 {
  const walk = fogContext?.round?.enemyPlannedWalks.find(w => w.playerId === target.id);
  if (walk && tuningFor(config).flow.leadPlannedWalks) {
    return getPlayerWorldPos({ ...target, surfaceDir: walk.surfaceDir }, planets);
  }
  return getPlayerWorldPos(target, planets);
}

// =============================================================================
// WEAPON SELECTION
// =============================================================================

/**
 * Compute personality-based base weight for a weapon (used when no target context).
 */
function getPersonalityWeight(key: WeaponKey, personality: BotPersonality, skill: number, tw: BotTuning['weapon']): number {
  const riskSkill = personality.riskTolerance * skill;
  const p = tw.preference[key];
  if (p.nukeRiskGate !== undefined && !(riskSkill > p.nukeRiskGate)) {
    return Math.max(tw.minWeight, p.nukeOff ?? tw.minWeight);
  }
  let w = p.base;
  if (p.aggression !== undefined) w += personality.aggression * p.aggression;
  if (p.invAggression !== undefined) w += (1 - personality.aggression) * p.invAggression;
  if (p.mobility !== undefined) w += personality.mobility * p.mobility;
  if (p.invMobility !== undefined) w += (1 - personality.mobility) * p.invMobility;
  if (p.risk !== undefined) w += personality.riskTolerance * p.risk;
  if (p.invRisk !== undefined) w += (1 - personality.riskTolerance) * p.invRisk;
  if (p.riskSkillGate !== undefined && riskSkill > p.riskSkillGate) w += p.riskSkillBonus ?? 0;
  return Math.max(tw.minWeight, w);
}

/**
 * Pick a weapon based on personality × skill, ammo availability, and target context.
 * Backward compatible: works without target/gameState (personality-only fallback).
 */
export function selectWeapon(
  player: PlayerState,
  config: BotConfig,
  rng: SeededRandom,
  target?: PlayerState,
  gameState?: GameState,
  fogContext?: BotFogContext
): WeaponKey {
  // Command override: explicit weapon or probe command
  const cmd = player.pendingCommand;
  if (cmd?.type === 'fireProbe' && hasAmmo(player, 'probe')) return 'probe';
  if (cmd?.weaponKey && hasAmmo(player, cmd.weaponKey)) return cmd.weaponKey;

  // §12.7: a field-class weapon reaches its own planet only — never for a target elsewhere or for blind fire
  const reachable = getAvailableWeapons(player, gameState).filter(k =>
    WEAPONS[k].rangeClass !== 'field' || (target !== undefined && target.planetIndex === player.planetIndex));
  const available = reachable.length > 0 ? reachable : getAvailableWeapons(player, gameState).filter(k => WEAPONS[k].rangeClass !== 'field');
  if (available.length === 0) return 'standard';
  if (available.length === 1) return available[0];

  const { personality, skill } = config;
  const tuning = tuningFor(config);
  const tw = tuning.weapon;
  // Round flows: an enemy that can still walk this round rewards a wider blast
  const round = fogContext?.round;
  const uncommittedBlast = round && target && !round.enemyCommittedIds.includes(target.id) ? tuning.flow.uncommittedBlastWeight : 0;

  // Chaotic: random weapon
  if (personality.riskTolerance > tw.chaoticMinRisk && personality.mobility > tw.chaoticMinMobility) {
    return available[rng.int(0, available.length - 1)];
  }

  // Build preference weights — start with personality base
  const weights: Partial<Record<WeaponKey, number>> = {};
  for (const key of available) {
    weights[key] = getPersonalityWeight(key, personality, skill, tw);
  }

  // Apply context scoring if target provided and skill >= 0.4
  if (target && gameState && skill >= tw.contextMinSkill) {
    const myPos = getPlayerWorldPos(player, gameState.planets);
    const targetPos = getPlayerWorldPos(target, gameState.planets);
    const dist = vecLength(vecSub(targetPos, myPos));

    for (const key of available) {
      let w = weights[key]!;
      const weapon = WEAPONS[key];

      // Probe exclusion: never fire offensively
      if (key === 'probe') {
        weights[key] = tw.minWeight;
        continue;
      }
      // Zero-damage, non-knockback utility weapons (tether, terraformer) are not attacks
      if (weapon.damage === 0 && !weapon.knockbackForce && !weapon.cluster) {
        weights[key] = tw.minWeight;
        continue;
      }

      // Overkill penalty: don't nuke nearly-dead enemies
      if (target.health <= tw.overkillTargetHealth && weapon.damage > tw.overkillDamage) {
        w *= tw.overkillPenalty;
      }

      // Hitscan preference: strongly prefer at close range (same planet or < 15 units)
      if (weapon.hitscan) {
        if (target.planetIndex === player.planetIndex && dist < tw.hitscanSamePlanetRange) {
          w += tw.hitscanSamePlanetBonus; // dominant at close range
        } else if (dist < tw.hitscanCloseRange) {
          w += tw.hitscanCloseBonus;
        } else {
          w *= tw.hitscanFarPenalty; // penalize at long range — hitscan requires line of sight
        }
      }

      // §12.7: the field class is the same-planet launcher — the lob that does not graze
      if (weapon.rangeClass === 'field' && target.planetIndex === player.planetIndex) w += tw.fieldSamePlanetBonus;

      // Distance bonus: far targets benefit from larger blast radius
      const distFactor = Math.min(1, dist / tw.distanceNorm);
      w += distFactor * weapon.blastRadius / tw.blastNorm;
      if (uncommittedBlast > 0) w += uncommittedBlast * weapon.blastRadius / tw.blastNorm;

      // Ammo conservation: save scarce ammo
      const ammo = player.loadout.ammo[key];
      if (ammo !== null && ammo !== undefined && ammo <= tw.scarceAmmo && key !== 'standard') {
        w *= tw.scarceAmmoPenalty;
      }

      // Collateral avoidance: avoid large blast near teammates
      if (weapon.blastRadius > tw.collateralBlastRadius) {
        const hasTeammateOnTargetPlanet = gameState.players.some(p =>
          p.id !== player.id && p.alive &&
          p.teamIndex === player.teamIndex &&
          p.planetIndex === target.planetIndex
        );
        if (hasTeammateOnTargetPlanet) {
          w *= tw.collateralPenalty;
        }
      }

      weights[key] = Math.max(tw.minWeight, w);
    }
  }

  // Weighted random selection
  const totalWeight = available.reduce((sum, k) => sum + (weights[k] ?? 1), 0);
  let roll = rng.next() * totalWeight;
  for (const key of available) {
    roll -= weights[key] ?? 1;
    if (roll <= 0) return key;
  }

  return available[available.length - 1];
}

// =============================================================================
// TARGETABLE ENEMIES (FOG)
// =============================================================================

/**
 * Get targetable enemies: visible + ghost-known, deduplicated.
 * Without fogContext, returns all alive enemies (current behavior).
 */
export function getTargetableEnemies(
  player: PlayerState,
  gameState: GameState,
  fogContext?: BotFogContext
): PlayerState[] {
  const allEnemies = getAliveEnemies(player, gameState.players);
  if (!fogContext) return allEnemies;

  const result: PlayerState[] = [];
  const addedIds = new Set<number>();

  // Add visible enemies
  for (const id of fogContext.visibleEnemyIds) {
    const enemy = allEnemies.find(e => e.id === id);
    if (enemy) {
      result.push(enemy);
      addedIds.add(id);
    }
  }

  // Add ghost targets (enemies not directly visible but with stale position info) — as the team BELIEVES them:
  // standing where they were last seen (37.20 fix a: the live position was read here before, so a bot shot at
  // a unit that had walked away exactly where it really stood)
  for (const ghost of fogContext.ghosts) {
    if (!addedIds.has(ghost.playerId)) {
      const enemy = allEnemies.find(e => e.id === ghost.playerId);
      if (enemy) {
        result.push(ghostBelief(enemy, ghost, gameState.planets));
        addedIds.add(ghost.playerId);
      }
    }
  }

  // §15.2 (GameConfig.teamPicture): the team picture's identified tracks — teammates', probes', drones', towers' —
  // as the team believes them; a newer track replaces an older ghost of the same unit
  for (const t of fogContext.pictureTargets ?? []) {
    if (t.unitId === undefined || fogContext.visibleEnemyIds.includes(t.unitId)) continue;
    const enemy = allEnemies.find(e => e.id === t.unitId);
    if (!enemy) continue;
    const belief: PlayerState = { ...enemy, planetIndex: t.planetIndex, surfaceDir: { ...t.surfaceDir }, airborne: false, airbornePos: null, airborneVel: null };
    if (!addedIds.has(t.unitId)) { result.push(belief); addedIds.add(t.unitId); continue; }
    const ghost = fogContext.ghosts.find(g => g.playerId === t.unitId);
    if (ghost && t.turnSeen > ghost.turnSeen) { const i = result.findIndex(r => r.id === t.unitId); if (i >= 0) result[i] = belief; }
  }

  return result;
}

/**
 * K3 (37.20 ledger #2, #3, #6): the enemies a bot knows about — visible ones as they are, remembered ones as
 * their ghosts say (ghostBelief), nobody else. Without a fog context (bot fog off, a match setting) every living
 * enemy: that is the rule the setting asks for, not a leak.
 */
export function knownEnemies(player: PlayerState, gameState: GameState, fogContext?: BotFogContext): PlayerState[] {
  return getTargetableEnemies(player, gameState, fogContext);
}

/**
 * 37.20: an enemy as its ghost says it is — the live unit with its planet and surface direction replaced by
 * the last-seen position (nearest planet to the marker), grounded. Health and the rest stay live: the HUD
 * shows every unit's health, seen or not, so a bot may read it too.
 */
export function ghostBelief(enemy: PlayerState, ghost: GhostMarker, planets: PlanetState[]): PlayerState {
  let planetIndex = enemy.planetIndex, best = Infinity;
  for (let i = 0; i < planets.length; i++) {
    const p = planets[i];
    if (!p.alive || p.type !== 'planet') continue;
    const d = vecLength(vecSub(ghost.position, p.position)) - p.radius;
    if (d < best) { best = d; planetIndex = i; }
  }
  const planet = planets[planetIndex];
  const surfaceDir = planet ? vecNormalize(vecSub(ghost.position, planet.position)) : enemy.surfaceDir;
  return { ...enemy, planetIndex, surfaceDir, airborne: false, airbornePos: null, airborneVel: null };
}
